// Polyfills
import '@babel/polyfill';
import 'app/common/polyfills/element-closest';
import 'event-source-polyfill/src/eventsource';
import 'intersection-observer';
import 'intl';
import 'url-polyfill';
import 'app/scripts/vendor/ChildNode.remove.polyfill'; // custom addition on line 12: if (this.parentNode !== null)
import 'whatwg-fetch';

import 'app/scripts/init';

// Extend jQuery with the format and formatHtml methods
import 'app/scripts/lib/jquery-localize';

import React from 'react';
import ReactDOM from '@trello/react-dom-wrapper';

import API from 'app/gamma/src/api';
import { defaultStore } from 'app/gamma/src/defaultStore';
import { setMember } from 'app/gamma/src/modules/state/models/session';
import { loadMemberProfile } from 'app/gamma/src/modules/loaders/load-member';
import experiment from 'app/gamma/src/util/experiments';

import { Storage } from '@trello/storage';
import { memberId } from '@trello/session-cookie';
import {
  Chrome,
  generateSupportDebugData,
  beginListeningForOptionalDebugEvents,
} from 'app/scripts/views/chrome';
import { recordFeatureFlags } from 'app/scripts/init/record-feature-flags';
import ModelCache from 'app/scripts/db/model-cache.coffee';
import { Analytics } from '@trello/atlassian-analytics';
import MepFfsClientSpike from 'app/scripts/mep-ffs-client-spike/mep-ffs-client-spike';
import { featureFlagClient } from '@trello/feature-flag-client';

beginListeningForOptionalDebugEvents();
window.__TRELLO_SUPPORT = generateSupportDebugData;

// Redux store setup, included here so that we can safely assume it's present early
experiment.setStore(defaultStore);
Storage.setStore(defaultStore);
if (memberId) {
  defaultStore.dispatch(setMember({ idMe: memberId }));
  defaultStore.dispatch(loadMemberProfile(memberId));
}

// Only used by TeamX for cross join analytics.
const isCrossJoinEnabled = featureFlagClient.get('team-x-cross-join', false);
if (isCrossJoinEnabled && memberId) {
  const localStorageKey = 'trackedUsers';
  const setTrackedUsers = (value) => {
    const trackedUsers = Storage.get(localStorageKey) || {};
    Storage.set(
      localStorageKey,
      Object.assign(trackedUsers, { [memberId]: value }),
    );
  };

  const shouldSend = () => {
    const trackedUsers = Storage.get(localStorageKey);
    return (
      !trackedUsers ||
      !trackedUsers[memberId] ||
      trackedUsers[memberId] === false
    );
  };

  let unsubscribe = undefined;

  unsubscribe = defaultStore.subscribe(() => {
    const me = defaultStore.getState().models.members[memberId];
    const hasLoaded = (member) =>
      member &&
      member.isAaMastered !== undefined &&
      member.confirmed !== undefined &&
      member.logins !== undefined;

    if (hasLoaded(me) && shouldSend()) {
      const trackEvent = {
        source: 'app',
        action: 'initialized',
        actionSubject: 'member',
        actionSubjectId: me.id,
        attributes: {
          isAaMastered: me.isAaMastered,
          isEmailVerified: me.confirmed,
          hasMultiEmail: me.logins.length > 1,
          isEnterpiseManaged: me.idEnterprise ? true : false,
        },
      };

      Analytics.sendTrackEvent(trackEvent);
      setTrackedUsers(true);
      if (unsubscribe) {
        unsubscribe();
      }
    }
  });
}

ReactDOM.render(<Chrome />, document.getElementById('chrome-container'));

// Write current feature flags to localStorage so we can send them along with
// any support requests
recordFeatureFlags();

// MEP Spike for understanding Trello traffic
// https://hello.atlassian.net/wiki/spaces/MEASURE/pages/570278102/Understanding+Trello+load+on+FFS
const mepFfsEnabledFlag = 'mep.ffs.enabled';
const mepPollingFlag = 'mep.ffs.pollingIntervalMins';
const pollingIntervalMinsDefault = 5;
featureFlagClient.client
  .waitForInitialization()
  .then(() => {
    const mepFfsEnabled = featureFlagClient.get(mepFfsEnabledFlag, false);
    const pollingValue = featureFlagClient.get(
      mepPollingFlag,
      pollingIntervalMinsDefault,
    );
    const mepFfsClientSpike = new MepFfsClientSpike(
      featureFlagClient.getUserKey(),
      pollingValue,
    );

    if (mepFfsEnabled) {
      mepFfsClientSpike.initialiseAndPoll();
    }

    featureFlagClient.on(mepFfsEnabledFlag, (isEnabled) => {
      isEnabled
        ? mepFfsClientSpike.enablePolling()
        : mepFfsClientSpike.disablePolling();
      mepFfsClientSpike.poll();
    });

    featureFlagClient.on(mepPollingFlag, (newValue) => {
      mepFfsClientSpike.updatePollingValue(newValue);
    });
  })
  .catch(() => {
    // Error occurred trying initialising the LaunchDarkly client
    // Ignore this for the purpose of the MEP spike
  });
